import React, { useMemo } from 'react';
import CampaignEmailLegalFooter, {
  CampaignEmailCopyrightFooter,
} from './CampaignEmailLegalFooter';
import {
  HUBUP_NOTIFICATION_BUTTON_VARIABLE,
  buildEditorHubupPreviewFromDesign,
  campaignEmailHtmlForPreview,
  renderCampaignEmailTemplate,
  templateUsesHubupVariable,
} from '../../utils/campaignEmailTemplate';
import { buildProductCardEmailHtml } from '../../utils/productCardEmail';
import { defaultHubupButtonDesign } from '../../utils/emailButtonStyles';
import InfoTooltip from '../ui/InfoTooltip';

const PREVIEW_PAYMENT_URL = '#';

const PLACEHOLDER_PRODUCT = {
  name: 'Nom du produit',
  description: '<p>Description du produit telle qu’en base.</p>',
  price: 49,
  imageUrl: '',
};

export default function CampaignEmailLivePreview({
  subject = '',
  html = '',
  bannerUrl = '',
  className = '',
  sticky = true,
  productPreview = null,
  productCardOptions = null,
  includeProductCard = false,
  hubupNotification = null,
  hubupButtonDesign = null,
  emailPreviewMode = 'editor',
  variant = 'full',
}) {
  const isThumb = variant === 'thumb';
  const previewHtml = useMemo(() => campaignEmailHtmlForPreview(html), [html]);
  const usesHubup = useMemo(() => templateUsesHubupVariable(html), [html]);
  const showProductCard = includeProductCard || Boolean(productCardOptions?.includeProductCard);

  const productCardHtml = useMemo(() => {
    if (!showProductCard) return '';
    const base = productPreview?.name || productPreview?.imageUrl
      ? productPreview
      : PLACEHOLDER_PRODUCT;
    return buildProductCardEmailHtml(base, {
      ...(productCardOptions || {}),
      paymentUrl: PREVIEW_PAYMENT_URL,
    });
  }, [showProductCard, productPreview, productCardOptions]);

  const hubupNotifForRender = useMemo(() => {
    if (!usesHubup) return null;
    if (hubupNotification) return hubupNotification;
    return buildEditorHubupPreviewFromDesign(
      hubupButtonDesign || defaultHubupButtonDesign(),
    );
  }, [usesHubup, hubupNotification, hubupButtonDesign]);

  const renderedHtml = useMemo(
    () =>
      renderCampaignEmailTemplate(previewHtml, {
        hubupNotification: hubupNotifForRender,
        includeHubup: usesHubup,
        includeProductCard: showProductCard,
        productCardHtml,
      }),
    [previewHtml, hubupNotifForRender, usesHubup, showProductCard, productCardHtml],
  );

  const subjectLine =
    String(subject || '').trim() || 'Objet de votre email';

  const mailInner = (
    <div className="overflow-hidden rounded-lg border border-border/60 bg-white shadow-sm">
      {bannerUrl ? (
        <div className="border-b border-border/40 px-2 py-2 flex justify-center bg-white">
          <img
            src={bannerUrl}
            alt=""
            className={`w-auto max-w-full object-contain ${isThumb ? 'max-h-16' : 'max-h-40'}`}
          />
        </div>
      ) : null}
      <div className={isThumb ? 'px-2 py-2 max-h-[140px] overflow-hidden' : 'px-4 py-3'}>
        <div
          className={`text-foreground space-y-0.5 [&_a]:text-primary ${
            isThumb ? 'text-[11px] leading-snug' : 'text-sm'
          }`}
          style={{ lineHeight: isThumb ? 1.35 : 1.4 }}
          dangerouslySetInnerHTML={{ __html: renderedHtml }}
        />
      </div>
      {!isThumb ? <CampaignEmailLegalFooter /> : null}
    </div>
  );

  if (isThumb) {
    return (
      <div className={className}>
        <p className="truncate px-2 pt-2 text-[10px] font-medium text-muted-foreground">
          {subjectLine}
        </p>
        <div className="p-2">{mailInner}</div>
      </div>
    );
  }

  return (
    <div className={className}>
      <p className="mb-2 flex flex-wrap items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Aperçu en direct
        <InfoTooltip text="Comme le mail envoyé : le bouton HubUP apparaît à l’emplacement de la variable dans le texte ; la fiche produit en fin de message si la case est cochée." />
      </p>
      <div
        className={`rounded-xl border border-border bg-[#f2f2f2] p-3 md:p-4 ${
          sticky ? 'lg:sticky lg:top-4' : ''
        }`}
      >
        <div className="mb-3 rounded-lg border border-border/60 bg-white px-3 py-2 text-left shadow-sm">
          <p className="text-[10px] uppercase text-muted-foreground mb-0.5">Objet</p>
          <p className="text-sm font-semibold text-foreground truncate">{subjectLine}</p>
        </div>
        <div className="flex justify-center">
          <div className="w-full max-w-[520px]">
            {mailInner}
            <CampaignEmailCopyrightFooter />
          </div>
        </div>
      </div>
    </div>
  );
}

export function CampaignEmailVariablesLegend({
  className = '',
  showHubup = true,
}) {
  return (
    <div
      className={`rounded-lg border border-dashed border-border bg-muted/20 p-3 text-xs text-muted-foreground space-y-2 ${className}`}
    >
      <p className="font-medium text-foreground text-sm flex items-center gap-1">
        Variable HubUP
        <InfoTooltip text="Placez %hubup_notification_button% dans le texte où vous voulez le bouton. Liez une notification publiée dans Options (template) ou à l’envoi." />
      </p>
      {showHubup ? (
        <p className="flex flex-wrap items-center gap-1 m-0">
          <code className="rounded bg-muted px-1 py-0.5 text-[11px] text-foreground">
            {HUBUP_NOTIFICATION_BUTTON_VARIABLE}
          </code>
          <InfoTooltip text="Bouton vers la notification HubUP choisie. Le contenu du mail se finalise à l’étape d’aperçu avant envoi." />
        </p>
      ) : (
        <p className="m-0 text-muted-foreground">
          Compte ou campagne HubUP requis pour utiliser le bouton notification.
        </p>
      )}
    </div>
  );
}
